import { Box, Avatar, Chip, LinearProgress, IconButton, Tooltip } from '@mui/material';
import { Text } from '@qwickapps/react-framework';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import RefreshIcon from '@mui/icons-material/Refresh';
import MarkdownContent from './MarkdownContent';
import ThoughtBubbles from './ThoughtBubbles';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  status?: 'sending' | 'sent' | 'error' | 'streaming' | 'pending' | 'running' | 'retrying' | 'failed';
  agentName?: string;
  promptId?: string;
  executionId?: string;
  executionStatus?: 'pending' | 'running' | 'retrying' | 'failed' | 'completed';
  errorMessage?: string;
  canRetry?: boolean;
  thoughts?: string[];
}

export interface ResponseBubbleProps {
  message: Message;
  showAvatar?: boolean;
  onRetry?: () => void;
}

export default function ResponseBubble({
  message,
  showAvatar = true,
  onRetry,
}: ResponseBubbleProps) {
  const { content, timestamp, status = 'sent', agentName, thoughts, canRetry, errorMessage } = message;

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        flexDirection: 'row',
        mb: 2,
      }}
    >
      {showAvatar && (
        <Avatar
          sx={{
            bgcolor: 'var(--theme-secondary-light)',
            width: 40,
            height: 40,
          }}
        >
          <SmartToyIcon sx={{ color: 'var(--theme-on-secondary)' }} />
        </Avatar>
      )}

      <Box
        sx={{
          flex: 1,
          maxWidth: '80%',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 0.5,
          }}
        >
          <Text variant="caption" customColor="var(--theme-on-primary)" style={{ fontWeight: 600 }}>
            {agentName || 'QwickForge'}
          </Text>
          <Text variant="caption" customColor="var(--theme-on-primary)">
            {timestamp.toLocaleTimeString()}
          </Text>
          {status === 'streaming' && (
            <Chip label="Streaming..." size="small" sx={{ height: 20, backgroundColor: 'var(--theme-primary)', color: 'var(--theme-on-primary)' }} />
          )}
          {status === 'running' && (
            <Chip label="Running" size="small" sx={{ height: 20, backgroundColor: 'var(--theme-info)', color: 'var(--theme-on-primary)' }} />
          )}
          {status === 'retrying' && (
            <Chip label="Retrying" size="small" sx={{ height: 20, backgroundColor: 'var(--theme-warning)', color: 'var(--theme-text-primary)' }} />
          )}
          {status === 'failed' && (
            <Chip label="Failed" size="small" sx={{ height: 20, backgroundColor: 'var(--theme-error)', color: 'var(--theme-on-error)' }} />
          )}
          {canRetry && onRetry && (
            <Tooltip title="Retry execution">
              <IconButton size="small" onClick={onRetry} sx={{ ml: 1 }}>
                <RefreshIcon fontSize="small" sx={{ color: 'var(--theme-text-secondary)' }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Thought Bubbles (only for assistant messages with thoughts) */}
        {thoughts && thoughts.length > 0 && (
          <ThoughtBubbles thoughts={thoughts} autoCollapse={status === 'sent'} />
        )}

        {/* Content */}
        <Box
          sx={{
            bgcolor: 'var(--theme-secondary-light)',
            color: 'var(--theme-on-primary)',
            p: 2,
            borderRadius: 2,
            border: 1,
            borderColor: status === 'failed' ? 'var(--theme-error)' : 'var(--theme-border-light)',
            boxShadow: 1,
          }}
        >
          {(status === 'streaming' || status === 'running') && (
            <LinearProgress sx={{ mb: 1, borderRadius: 1, '& .MuiLinearProgress-bar': { backgroundColor: 'var(--theme-primary)' } }} />
          )}

          {typeof content === 'string' ? (
            <MarkdownContent content={content} isUser={false} />
          ) : (
            content
          )}

          {/* Error message details */}
          {errorMessage && status === 'failed' && (
            <Box
              sx={{
                mt: 2,
                p: 1.5,
                bgcolor: 'var(--theme-error)',
                color: 'var(--theme-on-error)',
                borderRadius: 1,
                fontSize: '0.875rem',
              }}
            >
              <Text variant="caption" customColor="var(--theme-on-error)" style={{ fontWeight: 'bold', display: 'block', marginBottom: 4 }}>
                Error Details:
              </Text>
              <Text variant="caption" customColor="var(--theme-on-error)" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}>
                {errorMessage}
              </Text>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
